import { useState, useRef, useEffect } from 'react';
import type { SelectOption } from './MobileSelect';
import * as S from './styles';
import { ChevronDownIcon } from '../../../assets/icons/ChevronDownIcon';
import ReactDOM from 'react-dom';

interface DesktopSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  title?: string;
  width?: string | number;
  showSelectedValue?: boolean;
}

export const DesktopSelect = ({
  options,
  value,
  onChange,
  title = 'Sort',
  width,
  showSelectedValue = false,
}: DesktopSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const selectedOption = options.find((option) => option.value === value);
  const optionListRef = useRef<HTMLDivElement>(null);

  const handleOpen = () => {
    setIsOpen((prev) => !prev);
    setTimeout(() => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownStyle({
          position: 'absolute',
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width,
          minWidth: 240,
          zIndex: 2000,
        });
      }
    }, 0);
  };
  const handleClose = () => setIsOpen(false);

  const handleSelect = (newValue: string) => {
    onChange(newValue);
    handleClose();
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (
        ref.current &&
        !ref.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        optionListRef.current &&
        !optionListRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      ref={ref}
      style={{
        position: 'relative',
        ...(width ? { width: typeof width === 'number' ? `${width}px` : width } : {}),
      }}
    >
      <S.SelectButton ref={buttonRef} onClick={handleOpen} type="button">
        {showSelectedValue && selectedOption ? selectedOption.label : title}
        <span>
          <ChevronDownIcon isOpen={isOpen} />
        </span>
      </S.SelectButton>
      {/* 
 NOTE: Using inline styles instead of Chakra props (bg, border, borderColor, etc.)
 because ReactDOM.createPortal renders the dropdown to document.body, 
 which is outside the ChakraProvider/Theme context.
 
 In shortcode app, this is especially problematic because ChakraProvider 
 exists inside Shadow DOM, but the portal renders outside of it.
 Chakra semantic tokens like bg="generic.0" won't work without theme context.
 */}
      {isOpen &&
        ReactDOM.createPortal(
          <S.OptionsList
            ref={optionListRef}
            style={{
              ...dropdownStyle,
              backgroundColor: '#ffffff',
              border: '1px solid #dcdcdc',
              borderRadius: '8px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              padding: '8px 12px',
              overflowY: 'auto',
              maxHeight: '300px',
              pointerEvents: 'auto',
            }}
          >
            {options.map((option, idx) => (
              <S.OptionItem
                key={option.value}
                isSelected={option.value === value}
                onClick={() => handleSelect(option.value)}
                style={{
                  padding: '15px 5px',
                  borderBottom: idx < options.length - 1 ? '1px solid #dcdcdc' : 'none',
                }}
              >
                {option.label}
                {option.value === value && <S.CheckIcon>✓</S.CheckIcon>}
              </S.OptionItem>
            ))}
          </S.OptionsList>,
          document.body,
        )}
    </div>
  );
};

export default DesktopSelect;
